import { useCallback } from "react";
import { useNavigate } from "react-router";

/**
 * Hook for safe backward navigation with fallback support.
 *
 * Provides a function that navigates back in browser history when available,
 * or falls back to a specified path when there's no history (e.g., direct link entry).
 *
 * @param fallbackPath - The path to navigate to if there's no browser history
 * @returns A callback function that safely navigates back
 *
 * @example
 * ```tsx
 * function IssueDetailPage() {
 *   const navigateBack = useSafeNavigateBack('/issues');
 *
 *   return (
 *     <IconButton onClick={navigateBack}>
 *       <ArrowBackIcon />
 *     </IconButton>
 *   );
 * }
 * ```
 */
export function useSafeNavigateBack(fallbackPath: string) {
  const navigate = useNavigate();

  return useCallback(() => {
    // Check if there's history to go back to
    // window.history.state.idx > 0 means we have entries before the current one
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      // No history available, use fallback path
      navigate(fallbackPath);
    }
  }, [navigate, fallbackPath]);
}
